/**
 * The part of a view the binding decision reads: which view it is, and the
 * rule on a rule view. A creation view's session is irrelevant to binding.
 */
type TakeoverView = { kind: "list" | "new" } | { kind: "rule"; ruleId: string };

/**
 * What the takeover remembers between views: which view opened it, the rule
 * it has already bound, and the rule it showed last.
 */
export interface TakeoverBindingState {
  enteredAs: "new" | "rule" | null;
  boundRuleId: string | null;
  lastRuleId: string | null;
}

export const INITIAL_TAKEOVER_BINDING: TakeoverBindingState = {
  enteredAs: null,
  boundRuleId: null,
  lastRuleId: null,
};

/**
 * Whether arriving at `view` should bind a rule to the host's placement.
 *
 * Two transitions bind, and the mere presence of a rule id is neither:
 *
 * - A build hands the author from the creation view to the rule it produced.
 * - "Save as a new rule" hands the author from one rule to its copy. Inside a
 *   takeover that is the only way from one rule to another, and the copy is
 *   the edit the author wanted for THIS upsell, so it becomes the pending rule.
 *
 * Opening straight onto a rule is the Edit affordance, which changes no
 * binding. The memory is cleared when the takeover closes, so editing a rule
 * after creating one cannot re-bind.
 */
export const nextTakeoverBinding = (
  previous: TakeoverBindingState,
  view: TakeoverView
): { state: TakeoverBindingState; bind: string | null } => {
  if (view.kind === "list") {
    return { state: INITIAL_TAKEOVER_BINDING, bind: null };
  }

  const enteredAs = previous.enteredAs ?? view.kind;
  const lastRuleId = view.kind === "rule" ? view.ruleId : previous.lastRuleId;
  const state = { ...previous, enteredAs, lastRuleId };

  if (view.kind !== "rule" || previous.boundRuleId === view.ruleId) {
    return { state, bind: null };
  }
  const builtHere = enteredAs === "new";
  const copiedHere =
    previous.lastRuleId !== null && previous.lastRuleId !== view.ruleId;
  if (!builtHere && !copiedHere) {
    return { state, bind: null };
  }

  return { state: { ...state, boundRuleId: view.ruleId }, bind: view.ruleId };
};
